/**
 * **Zuletzt gewählt gilt weiter** — Farbe und Schrift des zuletzt eingestellten
 * **Canvas-Elements** gelten für das nächste, das abgesetzt wird.
 *
 * Wer seinen Canvas in einer Handschrift führt, stellt sie einmal ein und nicht
 * an jedem Zettel neu (so macht es FigJam). Siehe CONTEXT.md.
 *
 * **Je Projekt und gerätelokal**, deshalb `localStorage` und kein Feld in der
 * Datenbank: Es ist eine Arbeitsgewohnheit und keine Eigenschaft des Canvas —
 * dieselbe Begründung wie beim **Ausschnitt** (`canvasViewport.ts`), dessen
 * Muster hier wiederholt wird.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`.
 */

import { CANVAS_NOTE_COLORS, DEFAULT_CANVAS_NOTE_COLOR } from './canvasNoteStyle'
import { CANVAS_NOTE_FONTS } from './canvasFonts'
import { DEFAULT_CANVAS_TEXT_SIZE, clampCanvasTextSize } from './canvasText'

export interface CanvasNoteDefaults {
  /** `null` nur beim **Text**: seine **Standard**-Tinte. */
  color: string | null
  font: string | null
  /** Nur beim **Text** — jede Größenänderung zählt (CONTEXT.md, „Zuletzt gewählt gilt weiter"). */
  fontSize?: number
}

/** Gelb in der Standardschrift — für einen Canvas, an dem noch nichts eingestellt wurde. */
export const CANVAS_NOTE_DEFAULTS: CanvasNoteDefaults = {
  color: DEFAULT_CANVAS_NOTE_COLOR,
  font: null,
}

/**
 * Der **Text** beginnt in der Tinte des Themes (`null`), nicht in Gelb — gelbe
 * Schrift auf hellem Grund wäre kaum zu lesen — und in Größe 24.
 */
export const CANVAS_TEXT_DEFAULTS: CanvasNoteDefaults = {
  color: null,
  font: null,
  fontSize: DEFAULT_CANVAS_TEXT_SIZE,
}

const defaultsFor = (kind: CanvasNoteDefaultsKind): CanvasNoteDefaults =>
  (kind === 'text' ? CANVAS_TEXT_DEFAULTS : CANVAS_NOTE_DEFAULTS)

const STORAGE_KEY_PREFIX = 'happytask:canvas:defaults:'

/**
 * Welche Art sich etwas merkt — Notiz, Memo und **Bereich** je für sich.
 *
 * Der Bereich ist seit 2026-09-06 dabei. Er merkt sich nur die FARBE (einen
 * Schrift-Knopf hat er nicht); das Feld `font` steht trotzdem in der Form und
 * trägt dort die Vorgabe, damit alle drei Arten dieselbe Gestalt haben.
 */
export type CanvasNoteDefaultsKind = 'note' | 'memo' | 'area' | 'text'

// Je Projekt UND je Art (CONTEXT.md, „Zuletzt gewählt gilt weiter"): Wer
// Notizen in Gelb mit Marker führt und Memos in Weiß mit Serifen, stellt
// beides einmal ein — nicht bei jedem Wechsel der Art neu.
const storageKeyFor = (projectId: string, kind: CanvasNoteDefaultsKind): string =>
  `${STORAGE_KEY_PREFIX}${projectId}:${kind}`

/**
 * Der Schlüssel aus der Zeit vor der Trennung nach Art. Er gehört der
 * **Notiz**: Damals merkte sich nur sie etwas, das Memo beginnt frisch.
 */
const legacyStorageKeyFor = (projectId: string): string => `${STORAGE_KEY_PREFIX}${projectId}`

/**
 * Wie die Art vor der Umbenennung hieß (`'sticky'`/`'text'`, ADR 0014).
 *
 * Der Schlüssel trägt den Namen der Art — mit ihm zöge eine Umbenennung sonst
 * die gemerkten Voreinstellungen mit sich fort, und wer Notizen in Gelb mit
 * Marker führte, fände nach dem Update wieder die Vorgabe vor. Die Zeilen in
 * der Datenbank wandern per Migration; ein Eintrag im `localStorage` wird
 * stattdessen beim Lesen geerbt, so wie der namenlose davor.
 */
const LEGACY_KIND: Partial<Record<CanvasNoteDefaultsKind, string>> = { note: 'sticky', memo: 'text' }

/**
 * `null` für Arten ohne früheren Namen — den **Bereich** gab es in den alten
 * Schlüsseln noch gar nicht.
 *
 * Ausdrücklich `null` und kein `${undefined}` im Schlüssel: Der träfe zwar auch
 * nie etwas, aber aus Versehen. Ein Blick in den `localStorage` zeigte dann
 * eine Suche nach `…:undefined`, und der nächste Mensch fragte sich, ob da
 * etwas kaputt ist.
 */
const legacyKindKeyFor = (projectId: string, kind: CanvasNoteDefaultsKind): string | null => {
  const legacy = LEGACY_KIND[kind]
  return legacy ? `${STORAGE_KEY_PREFIX}${projectId}:${legacy}` : null
}

/*
  Geprüft wird beim LESEN: Ein Eintrag kann aus einer Fassung mit anderen
  Farben oder Schriften stammen. Ein Zettel in einer Farbe, die es nicht gibt,
  wäre schlimmer als einer in Gelb — Unbekanntes fällt auf die Vorgabe zurück.
*/
function parseDefaults(raw: string, fallback: CanvasNoteDefaults): CanvasNoteDefaults {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return fallback
    const { color, font, fontSize } = parsed as { color?: unknown; font?: unknown; fontSize?: unknown }
    // `null` ist nur dort eine Farbe, wo die Vorgabe selbst `null` ist (Text).
    const knownColor = (color === null && fallback.color === null)
      || (typeof color === 'string' && CANVAS_NOTE_COLORS.some(c => c.key === color))
    const knownFont = font === null || (typeof font === 'string' && CANVAS_NOTE_FONTS.some(f => f.key === font))
    return {
      color: knownColor ? color as string | null : fallback.color,
      font: knownFont ? font as string | null : fallback.font,
      ...(fallback.fontSize !== undefined
        ? { fontSize: typeof fontSize === 'number' ? clampCanvasTextSize(fontSize) : fallback.fontSize }
        : {}),
    }
  } catch {
    return fallback
  }
}

/** Was der nächste Zettel dieser Art bekommt — gemerkt oder die Vorgabe. */
export function readCanvasNoteDefaults(projectId: string, kind: CanvasNoteDefaultsKind): CanvasNoteDefaults {
  let raw: string | null
  try {
    raw = localStorage.getItem(storageKeyFor(projectId, kind))
    // Geerbt wird in zwei Stufen: erst der Eintrag unter dem alten Namen der
    // Art, dann (nur für die Notiz) der namenlose aus der Zeit davor.
    const legacyKey = legacyKindKeyFor(projectId, kind)
    if (raw === null && legacyKey) raw = localStorage.getItem(legacyKey)
    if (raw === null && kind === 'note') raw = localStorage.getItem(legacyStorageKeyFor(projectId))
  } catch {
    return defaultsFor(kind)
  }
  return raw ? parseDefaults(raw, defaultsFor(kind)) : defaultsFor(kind)
}

/**
 * Merken, was zuletzt eingestellt wurde — Farbe und Schrift einzeln, damit
 * ein Farbwechsel die gemerkte Schrift nicht verliert.
 */
export function writeCanvasNoteDefaults(
  projectId: string,
  kind: CanvasNoteDefaultsKind,
  change: Partial<CanvasNoteDefaults>,
): void {
  const current = readCanvasNoteDefaults(projectId, kind)
  try {
    localStorage.setItem(storageKeyFor(projectId, kind), JSON.stringify({ ...current, ...change }))
  } catch {
    // Kein Speicher (privates Fenster, volles Kontingent). Die Vorgabe ist
    // Bequemlichkeit, kein Inhalt — das Scheitern darf niemanden stören.
  }
}
